import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import './Auth.css';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link');
      setVerifying(false);
      return;
    }

    // Verify token
    const verifyToken = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/verify-reset-token/${token}`);
        const data = await response.json();

        if (data.success) {
          setTokenValid(true);
          setEmail(data.email);
        } else {
          setError(data.message || 'Invalid or expired reset link');
        }
      } catch (err) {
        setError('Failed to verify reset link');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, newPassword: password }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/signin');
        }, 3000);
      } else {
        setError(data.message || 'Failed to reset password');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="auth-page-container">
        <div className="auth-page-main">
          <div className="auth-page-box">
            <div className="auth-page-header">
              <h2 className="auth-page-title">Verifying reset link...</h2>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="auth-page-container">
        <div className="auth-page-main">
          <div className="auth-page-box">
            <Link to="/signin" className="auth-page-back-link">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Back to Sign In
            </Link>
            <div className="auth-page-header">
              <h2 className="auth-page-title">Invalid Reset Link</h2>
            </div>
            <div className="auth-page-error">
              {error || 'This password reset link is invalid or has expired.'}
            </div>
            <div className="auth-page-footer">
              <p>
                <Link to="/forgot-password" className="auth-page-link">
                  Request a new reset link
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-page-container">
        <div className="auth-page-main">
          <div className="auth-page-box">
            <Link to="/signin" className="auth-page-back-link">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Back to Sign In
            </Link>
            <div className="auth-page-header">
              <h2 className="auth-page-title">Password Reset Successful</h2>
            </div>
            <div className="auth-page-success">
              Your password has been reset successfully. Redirecting to sign in...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page-container">
      <div className="auth-page-main">
        <div className="auth-page-box">
          <Link to="/signin" className="auth-page-back-link">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Sign In
          </Link>
          <div className="auth-page-header">
            <h2 className="auth-page-title">Reset Password</h2>
            <p className="auth-page-form-hint" style={{ marginTop: '0.5rem', textAlign: 'center' }}>
              Enter your new password for {email}
            </p>
          </div>

          <form className="auth-page-form" onSubmit={handleSubmit}>
            {error && (
              <div className="auth-page-error">
                {error}
              </div>
            )}

            <div className="auth-page-form-group">
              <label htmlFor="password">New Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter new password"
                disabled={loading}
                minLength={8}
                autoComplete="new-password"
              />
              <p className="auth-page-form-hint">
                Make sure it's at least 8 characters.
              </p>
            </div>

            <div className="auth-page-form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm new password"
                disabled={loading}
                minLength={8}
                autoComplete="new-password"
              />
            </div>

            <button type="submit" className="auth-page-button" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>

          <div className="auth-page-footer">
            <p>
              Remember your password?{' '}
              <Link to="/signin" className="auth-page-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
